'use client';

import { useEffect, useRef, useState } from 'react';

interface CounterProps {
  /** Final numeric value to count up to. Strings like "200+" are split into "200" and "+" suffix. */
  value: string | number;
  /** Animation duration in ms. */
  duration?: number;
  /** Optional class on the wrapping span. */
  className?: string;
}

function parse(value: string | number): { numeric: number | null; prefix: string; suffix: string } {
  const raw = String(value);
  const match = raw.match(/^(\D*)([\d.,]+)(\D*)$/);
  if (!match) return { numeric: null, prefix: '', suffix: '' };
  const numeric = Number(match[2].replace(/,/g, ''));
  if (!Number.isFinite(numeric)) return { numeric: null, prefix: '', suffix: '' };
  return { numeric, prefix: match[1] ?? '', suffix: match[3] ?? '' };
}

export function Counter({ value, duration = 1400, className }: CounterProps) {
  const { numeric, prefix, suffix } = parse(value);
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(numeric === null ? String(value) : '0');

  useEffect(() => {
    if (numeric === null) return;
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplay(String(numeric));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const elapsed = now - start;
          const progress = Math.min(1, elapsed / duration);
          // ease-out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(String(Math.round(numeric * eased)));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [numeric, duration]);

  return (
    <span ref={ref} className={className}>
      {numeric === null ? display : `${prefix}${display}${suffix}`}
    </span>
  );
}
